import { useEffect, useRef, useCallback } from "react";
import { logError } from "@/services/logging/logger";

interface UseBrowserBackButtonOptions {
  onBack: () => void | Promise<void>;
  enabled?: boolean;
}

const HISTORY_STATE_KEY = "confirmation_page_back_intercept";

export function useBrowserBackButton({
  onBack,
  enabled = true,
}: UseBrowserBackButtonOptions): void {
  const hasSetupHistoryRef = useRef(false);
  const isHandlingBackRef = useRef(false);
  const onBackRef = useRef(onBack);

  useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  const handlePopState = useCallback((event: PopStateEvent) => {
    const isOurState = event.state?.key === HISTORY_STATE_KEY;

    if (!isOurState && !isHandlingBackRef.current) {
      isHandlingBackRef.current = true;

      window.history.pushState(
        { key: HISTORY_STATE_KEY },
        "",
        window.location.href,
      );

      Promise.resolve(onBackRef.current())
        .catch((error) => {
          logError(
            "useBrowserBackButton",
            "Error handling browser back button",
            error instanceof Error ? error : new Error(String(error)),
            { error },
          );
          window.history.back();
        })
        .finally(() => {
          isHandlingBackRef.current = false;
        });
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      if (hasSetupHistoryRef.current) {
        if (window.history.state?.key === HISTORY_STATE_KEY) {
          window.history.replaceState(null, "", window.location.href);
        }
        hasSetupHistoryRef.current = false;
      }
      return;
    }

    if (!hasSetupHistoryRef.current) {
      window.history.pushState(
        { key: HISTORY_STATE_KEY },
        "",
        window.location.href,
      );
      hasSetupHistoryRef.current = true;
    }

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [enabled, handlePopState]);

  useEffect(() => {
    return () => {
      if (hasSetupHistoryRef.current) {
        if (window.history.state?.key === HISTORY_STATE_KEY) {
          window.history.replaceState(null, "", window.location.href);
        }
        hasSetupHistoryRef.current = false;
      }
    };
  }, []);
}
